import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Billing, Invoice } from "@/lib/types";
import { Skeleton } from "@/ui/States";
import { IdentityDialog, IdentityPanel, InvoicesPanel, UnpaidPanel } from "@/features/billing/panels";

export const Route = createFileRoute("/_app/o/$orgId/billing/invoices")({
  component: Documents,
  /* `?edit=identity` opens the address form.
     The banner about a missing address lives on the layout, above all three
     tabs, and its button has to reach this form. In the address rather than in
     a shared state so the link can be followed, pasted and reloaded. */
  validateSearch: (search: Record<string, unknown>): { edit?: "identity" } =>
    search.edit === "identity" ? { edit: "identity" } : {},
});

/**
 * What was invoiced before capacity was rented, and the address invoices are
 * made out to.
 *
 * # WHY THE CREDIT INVOICES ARE NOT HERE ANY MORE
 *
 * They were, above the usage ones, and the two were never the same kind of
 * thing: a credit invoice is a receipt for something bought a moment ago, and
 * a usage invoice is a bill for a month that has closed. Putting them in one
 * place meant the receipt for a purchase just made was two tabs from where
 * the customer landed after making it.
 *
 * So the credit purchases and their documents are on the first tab, beside
 * the balance they belong to. What is left here is the closed set — no
 * further usage invoice will ever be drawn — and the invoice address.
 *
 * The address is here rather than on the overview for the same reason as
 * before: it is asked about occasionally, and usually by whoever does the
 * books.
 */
function Documents() {
  const { orgId } = Route.useParams();
  const { edit } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [editing, setEditing] = useState(edit === "identity");

  const billing = useQuery({
    queryKey: ["billing", orgId],
    queryFn: ({ signal }) =>
      api.get<Billing>(`/organisations/${orgId}/billing`, undefined, signal),
  });
  const invoices = useQuery({
    queryKey: ["invoices", orgId],
    queryFn: ({ signal }) =>
      api.get<{ invoices: Invoice[] }>(
        `/organisations/${orgId}/billing/invoices`,
        undefined,
        signal,
      ),
  });

  const account = billing.data;
  if (!account) return <Skeleton className="h-64 w-full rounded-xl" />;

  const close = () => {
    setEditing(false);
    if (edit) void navigate({ search: {} });
  };

  const older = invoices.data?.invoices ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* The usage invoices, for as long as any of them exists.
          Hidden entirely on an account that never had one, rather than shown
          as an empty table with an explanation nobody needs: a customer who
          arrived after capacity was rented has no reason to learn that the
          platform once billed differently. */}
      {/* Anything still owed, above the full list. One of these invoices can
          still stop the account, so what is open comes before what is
          history. */}
      {account.unpaid.length > 0 && (
        <UnpaidPanel
          account={account}
          base={`/organisations/${orgId}/billing`}
          onPaid={() => void billing.refetch()}
        />
      )}

      {(older.length > 0 || invoices.isLoading) && (
        <div className="flex flex-col gap-2">
          <p className="t-meta max-w-prose">
            These are the monthly invoices for metered usage, from before capacity
            was rented. No further one will be issued; the ones here are payable as
            they always were.
          </p>
          <InvoicesPanel
            invoices={older}
            loading={invoices.isLoading}
            error={invoices.isError ? invoices.error : null}
            onRetry={() => void invoices.refetch()}
          />
        </div>
      )}

      <IdentityPanel account={account} onEdit={() => setEditing(true)} />

      <IdentityDialog
        orgId={orgId}
        base={`/organisations/${orgId}/billing`}
        open={editing}
        account={account}
        onClose={close}
        onSaved={() => {
          void billing.refetch();
          close();
        }}
      />
    </div>
  );
}
